import { useEffect, useMemo, useState } from 'react';
import { Check, ExternalLink, Loader2, RefreshCw, Search, X } from 'lucide-react';
import {
  COLLECTION_LABELS,
  MATURITY_LABELS,
  contentIndexItemKey,
  fetchContentIndex,
  type ContentIndexCollection,
  type ContentIndexItem,
} from '../../lib/contentIndex';

interface RecommendContentPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (items: ContentIndexItem[]) => void;
  /** When set, limits selection count (e.g. 1 for test-link flow). */
  maxSelections?: number;
  title?: string;
  confirmLabel?: string;
}

const COLLECTION_FILTERS: Array<{ value: ContentIndexCollection | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'insights', label: 'Insights' },
  { value: 'buildInPublic', label: 'Build in public' },
  { value: 'playbook', label: 'Playbook tools' },
  { value: 'playbookPosts', label: 'Playbook guides' },
  { value: 'leadMagnets', label: 'Lead magnets' },
];

export default function RecommendContentPickerModal({
  isOpen,
  onClose,
  onAdd,
  maxSelections,
  title = 'Pick from site',
  confirmLabel = 'Add to email',
}: RecommendContentPickerModalProps) {
  const [items, setItems] = useState<ContentIndexItem[]>([]);
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexError, setIndexError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [collectionFilter, setCollectionFilter] = useState<ContentIndexCollection | 'all'>('all');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const loadIndex = async () => {
    setIndexLoading(true);
    setIndexError(null);
    try {
      const idx = await fetchContentIndex();
      setItems(idx.items);
    } catch (e: unknown) {
      setIndexError(e instanceof Error ? e.message : 'Could not load content index');
    } finally {
      setIndexLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setCollectionFilter('all');
    setSelectedKeys(new Set());
    void loadIndex();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (collectionFilter !== 'all' && it.collection !== collectionFilter) return false;
      if (!q) return true;
      return (
        it.title.toLowerCase().includes(q) ||
        it.subtitle.toLowerCase().includes(q) ||
        (it.section ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, query, collectionFilter]);

  const selectedItems = useMemo(
    () => items.filter((it) => selectedKeys.has(contentIndexItemKey(it))),
    [items, selectedKeys]
  );

  const toggleItem = (it: ContentIndexItem) => {
    const key = contentIndexItemKey(it);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      if (maxSelections === 1) return new Set([key]);
      if (maxSelections != null && next.size >= maxSelections) return next;
      next.add(key);
      return next;
    });
  };

  const handleAdd = () => {
    if (selectedItems.length === 0) return;
    onAdd(selectedItems);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col min-h-0 min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recommend-content-picker-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="recommend-content-picker-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-2 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search published pages…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {COLLECTION_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setCollectionFilter(f.value)}
                className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                  collectionFilter === f.value
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-600'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {indexLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : indexError ? (
            <div className="p-4 text-sm text-red-700 dark:text-red-300 space-y-2">
              <p className="break-words">{indexError}</p>
              <button
                type="button"
                onClick={() => void loadIndex()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No published pages match.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {filtered.map((it) => {
                const key = contentIndexItemKey(it);
                const checked = selectedKeys.has(key);
                return (
                  <li key={key}>
                    <div
                      className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                        checked ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleItem(it)}
                        className={`mt-0.5 shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                          checked
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                        }`}
                        aria-label={checked ? `Deselect ${it.title}` : `Select ${it.title}`}
                      >
                        {checked ? <Check className="w-3.5 h-3.5" /> : null}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleItem(it)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {it.title}
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          {it.subtitle}
                        </p>
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400 dark:text-gray-500">
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {COLLECTION_LABELS[it.collection]}
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded font-medium ${
                              it.maturity >= 3
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                                : it.maturity >= 2
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                            title={MATURITY_LABELS[it.maturity]}
                          >
                            M{it.maturity}
                          </span>
                          {it.pubDate ? <span>{it.pubDate}</span> : null}
                          {it.section ? <span className="truncate">{it.section}</span> : null}
                        </div>
                      </button>
                      <a
                        href={it.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
                        aria-label={`Open ${it.title}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {selectedItems.length === 0
              ? maxSelections === 1
                ? 'Select a page'
                : 'Select one or more pages'
              : maxSelections === 1
                ? '1 selected'
                : `${selectedItems.length} selected`}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={selectedItems.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
            >
              {confirmLabel}
              {maxSelections === 1 || selectedItems.length === 0
                ? ''
                : ` (${selectedItems.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
